/**
 * SipConnectionManager - Gerencia conexão automática do worker SIP
 * quando agente faz login no dashboard
 */

import SipAgentWorker from '../workers/SipAgentWorker';
import { authenticatedFetch } from '../utils/authHeaders';

const DEFAULT_WS_PORT = 8088;
const DEFAULT_WSS_PORT = 8089;

class SipConnectionManager {
  constructor() {
    this.workers = new Map(); // inboxId => SipAgentWorker
    this.currentUser = null;
    this.initialized = false;

    console.log('[PLUGIN][SIP-CHANNEL] Connection manager initialized');
  }

  /**
   * Inicializa manager quando usuário faz login
   */
  async initialize(user, { force = false } = {}) {
    if (!user?.id || !user?.account_id) {
      console.warn(
        '[PLUGIN][SIP-CHANNEL] Cannot initialize without a valid user context'
      );
      return false;
    }

    const isSameUser =
      this.currentUser?.id === user.id &&
      this.currentUser?.account_id === user.account_id;

    if (this.initialized && isSameUser && !force) {
      if (this.workers.size === 0) {
        await this.loadSipInboxes();
      } else {
        console.log('[PLUGIN][SIP-CHANNEL] Already initialized');
      }

      return true;
    }

    if (this.initialized && !isSameUser) {
      this.disconnectAll();
    }

    this.currentUser = user;
    this.initialized = true;

    console.log('[PLUGIN][SIP-CHANNEL] Initializing for user:', user.id);

    // Busca inboxes SIP do usuário
    await this.loadSipInboxes({ force });
    return true;
  }

  /**
   * Busca inboxes SIP que o usuário tem acesso
   */
  async loadSipInboxes({ force = false } = {}) {
    try {
      const accountId = this.currentUser.account_id;
      const response = await authenticatedFetch(
        `/api/v1/accounts/${accountId}/inboxes`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to load inboxes (${response.status})`);
      }

      const data = await response.json();

      // Filtra apenas inboxes SIP
      const sipInboxes = data.payload.filter(
        inbox => inbox.channel_type === 'Channel::Sip'
      );

      console.log(
        '[PLUGIN][SIP-CHANNEL] Found SIP inboxes:',
        sipInboxes.length
      );

      // Conecta a cada inbox SIP
      await Promise.all(
        sipInboxes.map(inbox =>
          this.connectToInbox(inbox, { force }).catch(() => null)
        )
      );
    } catch (error) {
      console.error('[PLUGIN][SIP-CHANNEL] Error loading SIP inboxes:', error);
    }
  }

  /**
   * Conecta worker SIP para um inbox específico
   */
  async connectToInbox(inbox, { force = false } = {}) {
    try {
      if (!this.currentUser?.id || !this.currentUser?.account_id) {
        throw new Error('Missing current user context for SIP connection');
      }

      const hydratedInbox = await this.ensureInboxProviderConfig(inbox);

      const existingWorker = this.workers.get(inbox.id);

      if (existingWorker && !force) {
        return existingWorker;
      }

      if (existingWorker && force) {
        existingWorker.disconnect();
        this.workers.delete(inbox.id);
      }

      // Verifica se está em modo per_agent (assume per_agent por padrão quando não definido)
      const registrationMode =
        this.getProviderConfig(hydratedInbox)?.registration_mode ?? 'per_agent';

      if (registrationMode !== 'per_agent') {
        console.log(
          '[PLUGIN][SIP-CHANNEL] Inbox not in per_agent mode:',
          hydratedInbox.id
        );
        return null;
      }

      // Busca configuração do ramal do agente
      const extension = await this.getAgentExtension(hydratedInbox);

      if (!extension) {
        console.log(
          '[PLUGIN][SIP-CHANNEL] No extension configured for user in inbox:',
          hydratedInbox.id
        );
        return null;
      }

      console.log(
        '[PLUGIN][SIP-CHANNEL] Connecting to inbox:',
        hydratedInbox.id,
        'extension:',
        extension.extension
      );

      const providerConfig = this.getProviderConfig(hydratedInbox);

      // Cria e conecta worker
      const worker = new SipAgentWorker({
        server: providerConfig.sip_server,
        extension: extension.extension,
        password: extension.password,
        realm: providerConfig.sip_realm || providerConfig.sip_server,
        transport: this.buildTransportUrl(providerConfig),
        inboxId: hydratedInbox.id,
        accountId: this.currentUser.account_id,
      });

      await worker.connect();

      // Armazena worker
      this.workers.set(hydratedInbox.id, worker);

      console.log(
        '[PLUGIN][SIP-CHANNEL] Connected to inbox:',
        hydratedInbox.id
      );
      return worker;
    } catch (error) {
      console.error(
        '[PLUGIN][SIP-CHANNEL] Error connecting to inbox:',
        inbox.id,
        error
      );
      throw error;
    }
  }

  /**
   * Reconecta um inbox específico para o usuário atual
   */
  async reconnectInbox(inbox, user = this.currentUser) {
    if (user?.id && user?.account_id) {
      await this.initialize(user);
    }

    return this.connectToInbox(inbox, { force: true });
  }

  /**
   * Busca configuração do ramal do agente
   */
  async getAgentExtension(inbox) {
    try {
      const accountId = this.currentUser.account_id;
      const response = await authenticatedFetch(
        `/api/v1/accounts/${accountId}/inboxes/${inbox.id}/sip_extensions/${this.currentUser.id}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(
          `Failed to load SIP extension for inbox ${inbox.id} (${response.status})`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('[PLUGIN][SIP-CHANNEL] Error fetching extension:', error);
      return null;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  getProviderConfig(inbox) {
    return (
      inbox?.provider_config ||
      inbox?.providerConfig ||
      inbox?.channel?.provider_config ||
      inbox?.channel?.providerConfig ||
      null
    );
  }

  hasRequiredProviderConfig(inbox) {
    const config = this.getProviderConfig(inbox);
    return Boolean(config?.sip_server);
  }

  async ensureInboxProviderConfig(inbox) {
    if (this.hasRequiredProviderConfig(inbox)) {
      return inbox;
    }

    const accountId = this.currentUser?.account_id;
    if (!accountId || !inbox?.id) {
      return inbox;
    }

    const detailedInbox = await this.fetchInboxDetails(accountId, inbox.id);
    const mergedInbox = {
      ...inbox,
      ...detailedInbox,
      provider_config:
        this.getProviderConfig(detailedInbox) || this.getProviderConfig(inbox),
    };

    if (this.hasRequiredProviderConfig(mergedInbox)) {
      return mergedInbox;
    }

    const runtimeInbox = await this.fetchSipRuntimeConfig(accountId, inbox.id);
    const runtimeMergedInbox = {
      ...mergedInbox,
      ...runtimeInbox,
      provider_config:
        this.getProviderConfig(runtimeInbox) || this.getProviderConfig(mergedInbox),
    };

    if (!this.hasRequiredProviderConfig(runtimeMergedInbox)) {
      throw new Error(`SIP inbox ${inbox.id} is missing provider_config`);
    }

    return runtimeMergedInbox;
  }

  async fetchInboxDetails(accountId, inboxId) {
    const response = await authenticatedFetch(
      `/api/v1/accounts/${accountId}/inboxes/${inboxId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to load SIP inbox ${inboxId} (${response.status})`
      );
    }

    return response.json();
  }

  async fetchSipRuntimeConfig(accountId, inboxId) {
    const response = await authenticatedFetch(
      `/api/v1/accounts/${accountId}/inboxes/${inboxId}/sip_runtime`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to load SIP runtime config for inbox ${inboxId} (${response.status})`
      );
    }

    return response.json();
  }

  /**
   * Constrói URL de transporte WebSocket
   */
  // eslint-disable-next-line class-methods-use-this
  buildTransportUrl(config) {
    const protocol = config.sip_transport === 'wss' ? 'wss' : 'ws';
    const server = config.sip_server;
    const port =
      config.sip_port ||
      (protocol === 'wss' ? DEFAULT_WSS_PORT : DEFAULT_WS_PORT);

    return `${protocol}://${server}:${port}`;
  }

  /**
   * Desconecta todos os workers
   */
  disconnectAll() {
    console.log('[PLUGIN][SIP-CHANNEL] Disconnecting all workers...');

    this.workers.forEach((worker, inboxId) => {
      try {
        worker.disconnect();
        console.log('[PLUGIN][SIP-CHANNEL] Disconnected from inbox:', inboxId);
      } catch (error) {
        console.error(
          '[PLUGIN][SIP-CHANNEL] Error disconnecting from inbox:',
          inboxId,
          error
        );
      }
    });

    this.workers.clear();
    this.initialized = false;
    this.currentUser = null;
  }

  /**
   * Desconecta worker de um inbox específico
   */
  disconnectInbox(inboxId) {
    const worker = this.workers.get(inboxId);

    if (worker) {
      worker.disconnect();
      this.workers.delete(inboxId);
      console.log('[PLUGIN][SIP-CHANNEL] Disconnected from inbox:', inboxId);
    }
  }

  /**
   * Retorna worker de um inbox específico
   */
  getWorker(inboxId) {
    return this.workers.get(inboxId);
  }

  /**
   * Retorna todos os workers ativos
   */
  getAllWorkers() {
    return Array.from(this.workers.values());
  }

  /**
   * Retorna estado de conexão de todos os workers
   */
  getConnectionStatus() {
    const status = {};

    this.workers.forEach((worker, inboxId) => {
      status[inboxId] = worker.getState();
    });

    return status;
  }

  getCallSnapshots() {
    return Array.from(this.workers.values())
      .map(worker => worker.getCurrentCallSnapshot?.())
      .filter(Boolean);
  }
}

// Singleton instance
const sipConnectionManager = new SipConnectionManager();

// Auto-initialize quando window.chatwootConfig está disponível
if (window.chatwootConfig?.user) {
  sipConnectionManager.initialize(window.chatwootConfig.user);
}

// Desconecta ao fechar/recarregar página
window.addEventListener('beforeunload', () => {
  sipConnectionManager.disconnectAll();
});

// Expõe globalmente para debug e uso em componentes
window.sipConnectionManager = sipConnectionManager;

export default sipConnectionManager;
